import { useEffect, useRef } from 'react';
import { TrendingUp } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const fmt = (iso) => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const DashboardTrendChart = ({ t, trend = [] }) => {
  const labels = trend.map((r) => fmt(r.date));

  const data = {
    labels,
    datasets: [
      {
        label: t('attendance.present') || 'Présents',
        data: trend.map((r) => r.present),
        borderColor: '#27a644',
        backgroundColor: 'rgba(39,166,68,0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
      {
        label: t('attendance.absent') || 'Absents',
        data: trend.map((r) => r.absent),
        borderColor: '#e5484d',
        backgroundColor: 'rgba(229,72,77,0.06)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
      {
        label: t('attendance.late') || 'Retards',
        data: trend.map((r) => r.late),
        borderColor: '#f76b15',
        backgroundColor: 'rgba(247,107,21,0.06)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'var(--lm-s2, #1e1e2e)',
        titleColor: 'var(--lm-ink, #fff)',
        bodyColor: 'var(--lm-ink-2, #aaa)',
        borderColor: 'var(--lm-border, #333)',
        borderWidth: 1,
        padding: 10,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: 'var(--lm-ink-3, #888)', font: { size: 11 } },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'var(--lm-border, rgba(255,255,255,0.06))' },
        ticks: { color: 'var(--lm-ink-3, #888)', font: { size: 11 }, precision: 0 },
        border: { display: false },
      },
    },
  };

  const hasData = trend.some((r) => r.present > 0 || r.absent > 0 || r.late > 0);

  return (
    <div
      className="lg:col-span-4 rounded-xl overflow-hidden"
      style={{ backgroundColor: 'var(--lm-s1)', border: '1px solid var(--lm-border)' }}
    >
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: '1px solid var(--lm-border)' }}
      >
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--lm-ink)' }}>
            {t('dashboard.attendanceTrend') || 'Tendance d\'assiduité'}
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--lm-ink-2)' }}>
            {t('dashboard.last7Days') || '7 derniers jours'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {[
            { color: '#27a644', label: t('attendance.present') || 'Présents' },
            { color: '#e5484d', label: t('attendance.absent') || 'Absents' },
            { color: '#f76b15', label: t('attendance.late') || 'Retards' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              <span className="text-xs" style={{ color: 'var(--lm-ink-3)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 py-4" style={{ height: 220 }}>
        {hasData ? (
          <Line data={data} options={options} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-2">
            <TrendingUp className="w-8 h-8" style={{ color: 'var(--lm-border-2)' }} />
            <p className="text-sm" style={{ color: 'var(--lm-ink-2)' }}>
              {t('dashboard.noTrendData')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTrendChart;
